/**
 * Place search sheet for DayView Add Item (mirrors iOS PlaceSearchView).
 * Modes: Places | Tours | Manual. City is locked to the day being viewed.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Input, Spin, Button, Segmented } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { placeSearchEn, tourSearchEn } from '../../content/trips.en';
import {
  getPlaceDetails,
  inferItemType,
  isGooglePlacesConfigured,
  searchPlaces,
  type PlaceDetails,
  type PlaceSearchResult,
} from '../../services/googlePlacesService';
import {
  fetchFirstPageAndCache,
  getCachedViatorProducts,
  isOnline,
  toursLoadAction,
  type ViatorProduct,
} from '../../services/viatorProductsService';
import type { ItineraryItemType } from '../../types/client';
import TourDateSlotModal, {
  type TourDateSlotSelection,
} from '../TourDateSlotModal/TourDateSlotModal';
import styles from './PlaceSearchModal.module.css';

export type PlaceSearchMode = 'places' | 'tours' | 'manual';

export interface PlaceSearchCity {
  id: string;
  cityName: string;
  latitude?: number;
  longitude?: number;
}

export interface PlaceSearchSelection {
  place: PlaceDetails;
  suggestedType: Exclude<ItineraryItemType, 'accommodation' | 'other' | 'note'>;
  city: PlaceSearchCity;
}

export interface PlaceSearchTourAdd {
  tour: ViatorProduct;
  dateYmd: string;
  slot: TourDateSlotSelection['slot'];
  city: PlaceSearchCity;
}

interface PlaceSearchModalProps {
  open: boolean;
  cities: PlaceSearchCity[];
  initialCityId?: string | null;
  /** When set with userId, Tours segment is available. */
  enableTours?: boolean;
  userId?: string | null;
  arrivalDate?: string | null;
  departureDate?: string | null;
  initialTourDate?: string | null;
  tourAdding?: boolean;
  onClose: () => void;
  onPlaceSelected: (selection: PlaceSearchSelection) => void;
  onManualEntry: (city: PlaceSearchCity) => void;
  onTourAdd?: (payload: PlaceSearchTourAdd) => void;
}

function typeBadgeLabel(types: string[]): string {
  const inferred = inferItemType(types);
  if (inferred === 'meal') return placeSearchEn.typeMeal;
  if (inferred === 'transport') return placeSearchEn.typeTransport;
  return placeSearchEn.typeActivity;
}

function formatTourPrice(product: ViatorProduct): string | null {
  if (product.price?.startingPrice == null) return null;
  return `${product.price.currency} ${product.price.startingPrice.toFixed(0)}`;
}

const PlaceSearchModal: React.FC<PlaceSearchModalProps> = ({
  open,
  cities,
  initialCityId,
  enableTours = false,
  userId,
  arrivalDate,
  departureDate,
  initialTourDate,
  tourAdding = false,
  onClose,
  onPlaceSelected,
  onManualEntry,
  onTourAdd,
}) => {
  const configured = isGooglePlacesConfigured();
  const [mode, setMode] = useState<PlaceSearchMode>('places');
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState<PlaceSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const [tours, setTours] = useState<ViatorProduct[]>([]);
  const [isLoadingTours, setIsLoadingTours] = useState(false);
  const [toursError, setToursError] = useState<string | null>(null);
  const [selectedTour, setSelectedTour] = useState<ViatorProduct | null>(null);
  const toursRequestRef = useRef(0);

  const lockedCity = useMemo(() => {
    if (initialCityId) {
      const preferred = cities.find((c) => c.id === initialCityId);
      if (preferred) return preferred;
    }
    return cities[0] ?? null;
  }, [cities, initialCityId]);

  const toursAvailable = Boolean(enableTours && userId && lockedCity && onTourAdd);

  const modeOptions = useMemo(() => {
    const opts: { label: string; value: PlaceSearchMode }[] = [
      { label: placeSearchEn.modePlaces, value: 'places' },
    ];
    if (toursAvailable) {
      opts.push({ label: placeSearchEn.modeTours, value: 'tours' });
    }
    opts.push({ label: placeSearchEn.modeManual, value: 'manual' });
    return opts;
  }, [toursAvailable]);

  const modalTitle =
    mode === 'tours'
      ? placeSearchEn.titleTours
      : mode === 'manual'
        ? placeSearchEn.titleManual
        : placeSearchEn.title;

  useEffect(() => {
    if (!open) {
      setSelectedTour(null);
      return;
    }
    setMode('places');
    setSearchText('');
    setResults([]);
    setErrorMessage(configured ? null : placeSearchEn.missingApiKey);
    setIsSearching(false);
    setIsLoadingDetails(false);
    setTours([]);
    setToursError(null);
    setSelectedTour(null);
  }, [open, configured]);

  useEffect(() => {
    if (!open || mode !== 'places' || !configured || !lockedCity) return;
    const query = searchText.trim();
    if (!query) {
      setResults([]);
      setIsSearching(false);
      setErrorMessage(configured ? null : placeSearchEn.missingApiKey);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsSearching(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const places = await searchPlaces({
            textQuery: query,
            cityName: lockedCity.cityName,
            latitude: lockedCity.latitude,
            longitude: lockedCity.longitude,
          });
          if (requestId !== requestIdRef.current) return;
          setResults(places);
          setErrorMessage(null);
        } catch {
          if (requestId !== requestIdRef.current) return;
          setResults([]);
          setErrorMessage(placeSearchEn.searchError);
        } finally {
          if (requestId === requestIdRef.current) setIsSearching(false);
        }
      })();
    }, 300);

    return () => window.clearTimeout(timer);
  }, [open, mode, configured, searchText, lockedCity]);

  const loadTours = useCallback(
    async (forceNetwork = false) => {
      if (!userId || !lockedCity) {
        setTours([]);
        setToursError(tourSearchEn.noCity);
        return;
      }

      const requestId = ++toursRequestRef.current;
      setIsLoadingTours(true);
      setToursError(null);

      const cached = getCachedViatorProducts(userId, lockedCity.id);
      const online = isOnline();
      const action =
        forceNetwork && online
          ? 'fetchFirstPage'
          : toursLoadAction(cached.length > 0, online);

      if (action === 'showCache') {
        if (requestId !== toursRequestRef.current) return;
        setTours(cached);
        setIsLoadingTours(false);
        return;
      }

      if (action === 'showOfflineEmpty') {
        if (requestId !== toursRequestRef.current) return;
        setTours([]);
        setToursError(null);
        setIsLoadingTours(false);
        return;
      }

      try {
        const products = await fetchFirstPageAndCache(userId, lockedCity.id);
        if (requestId !== toursRequestRef.current) return;
        setTours(products);
        setToursError(null);
      } catch (err) {
        if (requestId !== toursRequestRef.current) return;
        setTours([]);
        setToursError(
          !isOnline()
            ? tourSearchEn.emptyOffline
            : err instanceof Error && err.message
              ? err.message
              : tourSearchEn.loadErrorTitle
        );
      } finally {
        if (requestId === toursRequestRef.current) setIsLoadingTours(false);
      }
    },
    [userId, lockedCity]
  );

  useEffect(() => {
    if (!open || mode !== 'tours' || !toursAvailable) return;
    void loadTours();
  }, [open, mode, toursAvailable, loadTours]);

  const handleSelect = async (result: PlaceSearchResult): Promise<void> => {
    if (!lockedCity || isLoadingDetails) return;
    setIsLoadingDetails(true);
    setErrorMessage(null);
    try {
      const details = await getPlaceDetails(result.id);
      onPlaceSelected({
        place: details,
        suggestedType: inferItemType(details.types.length ? details.types : result.types),
        city: lockedCity,
      });
    } catch {
      setErrorMessage(placeSearchEn.detailsError);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleManual = (): void => {
    if (!lockedCity) return;
    onManualEntry(lockedCity);
  };

  const handleTourConfirm = (selection: TourDateSlotSelection): void => {
    if (!selectedTour || !lockedCity || !onTourAdd) return;
    onTourAdd({
      tour: selectedTour,
      dateYmd: selection.dateYmd,
      slot: selection.slot,
      city: lockedCity,
    });
  };

  const renderPlaces = (): React.ReactNode => (
    <>
      <Input
        allowClear
        size="large"
        prefix={<SearchOutlined />}
        placeholder={placeSearchEn.searchPlaceholder}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        disabled={!configured || isLoadingDetails}
      />

      {errorMessage ? <div className={styles.error}>{errorMessage}</div> : null}

      <div className={styles.results}>
        {isSearching || isLoadingDetails ? (
          <div className={styles.center}>
            <Spin />
            <span>{placeSearchEn.searching}</span>
          </div>
        ) : !configured ? null : searchText.trim() && results.length === 0 ? (
          <div className={styles.muted}>{placeSearchEn.noResults}</div>
        ) : !searchText.trim() ? (
          <div className={styles.muted}>{placeSearchEn.prompt}</div>
        ) : (
          <ul className={styles.resultList}>
            {results.map((place) => (
              <li key={place.id}>
                <button
                  type="button"
                  className={styles.resultRow}
                  onClick={() => void handleSelect(place)}
                >
                  <div className={styles.resultMain}>
                    <div className={styles.resultName}>{place.name}</div>
                    {place.address ? (
                      <div className={styles.resultAddress}>{place.address}</div>
                    ) : null}
                  </div>
                  <span className={styles.typeBadge}>{typeBadgeLabel(place.types)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );

  const renderManual = (): React.ReactNode => (
    <div className={styles.manualPane}>
      <div className={styles.manualHeading}>{placeSearchEn.manualHeading}</div>
      <p className={styles.manualBody}>
        {placeSearchEn.manualBody.replace('{city}', lockedCity?.cityName ?? '')}
      </p>
      <Button type="primary" onClick={handleManual} disabled={!lockedCity}>
        {placeSearchEn.manualContinue}
      </Button>
    </div>
  );

  const renderTours = (): React.ReactNode => {
    if (isLoadingTours) {
      return (
        <div className={styles.center}>
          <Spin />
          <span>{tourSearchEn.loading}</span>
        </div>
      );
    }

    if (toursError) {
      return (
        <div className={styles.emptyPane}>
          <div className={styles.emptyTitle}>{tourSearchEn.loadErrorTitle}</div>
          <p className={styles.muted}>{toursError}</p>
          {isOnline() ? (
            <Button onClick={() => void loadTours(true)}>{tourSearchEn.retry}</Button>
          ) : null}
        </div>
      );
    }

    if (tours.length === 0) {
      return (
        <div className={styles.emptyPane}>
          <div className={styles.emptyTitle}>{tourSearchEn.emptyTitle}</div>
          <p className={styles.muted}>
            {!isOnline()
              ? tourSearchEn.emptyOffline
              : tourSearchEn.emptyForCity.replace('{city}', lockedCity?.cityName ?? '')}
          </p>
          {isOnline() ? (
            <Button onClick={() => void loadTours(true)}>{tourSearchEn.retry}</Button>
          ) : null}
        </div>
      );
    }

    return (
      <div className={styles.results}>
        <div className={styles.tourCount}>
          {tourSearchEn.countAvailable.replace('{count}', String(tours.length))}
        </div>
        <ul className={styles.resultList}>
          {tours.map((product) => {
            const price = formatTourPrice(product);
            return (
              <li key={product.productCode}>
                <button
                  type="button"
                  className={styles.tourRow}
                  onClick={() => setSelectedTour(product)}
                >
                  {product.imageUrl ? (
                    <img
                      className={styles.tourThumb}
                      src={product.imageUrl}
                      alt=""
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.tourThumbPlaceholder} aria-hidden />
                  )}
                  <div className={styles.resultMain}>
                    <div className={styles.resultName}>{product.title}</div>
                    {price ? <div className={styles.resultAddress}>{price}</div> : null}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      <Modal
        open={open}
        title={modalTitle}
        onCancel={onClose}
        footer={null}
        destroyOnClose
        width={520}
        className={styles.modal}
      >
        <div className={styles.body}>
          {lockedCity ? <div className={styles.singleCity}>{lockedCity.cityName}</div> : null}

          {modeOptions.length > 1 ? (
            <Segmented
              block
              options={modeOptions}
              value={mode}
              aria-label={placeSearchEn.modeLabel}
              onChange={(value) => setMode(value as PlaceSearchMode)}
            />
          ) : null}

          {mode === 'places' ? renderPlaces() : null}
          {mode === 'manual' ? renderManual() : null}
          {mode === 'tours' ? renderTours() : null}
        </div>
      </Modal>

      <TourDateSlotModal
        open={Boolean(selectedTour)}
        tour={selectedTour}
        arrivalDate={arrivalDate}
        departureDate={departureDate}
        initialDate={initialTourDate || arrivalDate || ''}
        adding={tourAdding}
        onCancel={() => {
          if (!tourAdding) setSelectedTour(null);
        }}
        onConfirm={handleTourConfirm}
      />
    </>
  );
};

export default PlaceSearchModal;
